import { useDebounce } from "@/hooks";
import { cn } from "@/utils";
import { motion, useReducedMotion } from "framer-motion";
import { Search, X } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import { controlBase } from "./field.styles";
import { Select, type SelectOption } from "./Select";

/**
 * Busca que só ocupa espaço quando está sendo usada.
 *
 * Fechada, é um botão redondo com a lupa. Aberta, é o campo — e o foco já está
 * dentro dele, porque quem clica na lupa quer digitar, não quer um segundo
 * clique. A lupa vira "x": o mesmo botão que abriu é o que limpa e fecha, então
 * não existe estado de "campo aberto com sobra de texto de ontem".
 *
 * Por que não deixar o campo sempre aberto: no cabeçalho da lista ele disputaria
 * a linha com "Filtros" e "Gerar planilha", e em 360px empurraria os dois para
 * baixo. Fechado, ele custa 44px — o mesmo de um botão de ícone.
 *
 * **A busca é a exceção à regra do "só aplica no clique".** Filtro mora no
 * `Modal` e só vale no "Aplicar"; busca por texto acontece enquanto se digita,
 * com atraso (`useDebounce`) para não disparar uma requisição por tecla. A
 * diferença é o que o usuário espera de cada um: escolher três filtros é uma
 * decisão, digitar um CPF é uma pergunta só.
 */

export type SearchScope = SelectOption & {
  /** Texto de apoio dentro do campo para este escopo. */
  placeholder?: string;
  /** Teclado do celular: `numeric` para documento, `tel` para telefone. */
  inputMode?: "text" | "numeric" | "tel" | "search";
  /** Máscara aplicada a cada tecla — o que o usuário vê. */
  format?: (valor: string) => string;
  /** O que vai para `onSearch` — normalmente só os dígitos. */
  parse?: (valor: string) => string;
};

export interface SearchFieldProps {
  /**
   * Chamado com o termo já normalizado pelo `parse` do escopo, depois do
   * atraso. Limpar e trocar de escopo avisam na hora, sem esperar.
   */
  onSearch: (termo: string, escopo: string) => void;
  /**
   * O que se procura: nome, documento, telefone. Com um escopo só, o seletor
   * não aparece — seria uma escolha sem alternativa.
   */
  scopes?: SearchScope[];
  defaultScope?: string;
  /**
   * Termo que já estava valendo quando a tela abriu — de um filtro guardado
   * entre visitas, por exemplo. Com ele a barra **nasce aberta e preenchida**:
   * uma lista filtrada por um termo invisível é a mesma armadilha do filtro
   * esquecido de ontem.
   *
   * Só semeia a primeira renderização, e não dispara `onSearch` — quem tinha o
   * termo guardado já filtrou a lista com ele.
   */
  defaultValue?: string;
  /** Rótulo do seletor para leitores de tela. */
  scopeLabel?: string;
  /** Nome acessível do botão fechado e do campo. */
  label?: string;
  placeholder?: string;
  /** Atraso do `useDebounce`, em ms. */
  delay?: number;
  /**
   * Sempre aberta, na largura do contêiner: para quando a busca é o assunto
   * da tela (o blog público) e não divide linha com outros botões. O "x" só
   * limpa, e só aparece com texto digitado.
   */
  alwaysOpen?: boolean;
  className?: string;
}

/** Duração de entrada de painel do DESIGN.md. */
const EASE_OUT = [0.16, 1, 0.3, 1] as const;

export const SearchField = ({
  onSearch,
  scopes = [],
  defaultScope,
  defaultValue = "",
  scopeLabel = "Buscar por",
  label = "Buscar",
  placeholder,
  delay = 400,
  alwaysOpen = false,
  className,
}: SearchFieldProps) => {
  const idBase = useId();
  const [aberto, setAberto] = useState(alwaysOpen || defaultValue !== "");
  const [termo, setTermo] = useState(defaultValue);
  const [escopo, setEscopo] = useState(
    defaultScope ?? scopes[0]?.value ?? "default"
  );

  const campoRef = useRef<HTMLInputElement>(null);
  const semMovimento = useReducedMotion();
  const duracao = (ms: number) => (semMovimento ? 0 : ms / 1000);

  const escopoAtual = scopes.find(item => item.value === escopo);
  const termoAdiado = useDebounce(termo, delay);

  // `onSearch` num ref: a tela quase sempre passa uma função nova a cada
  // render, e com ela na lista de dependências o efeito dispararia a busca em
  // todo render, não a cada troca de termo.
  const buscaRef = useRef(onSearch);
  buscaRef.current = onSearch;

  const normalizar = (valor: string) =>
    escopoAtual?.parse ? escopoAtual.parse(valor) : valor;

  // Nasce com o termo semeado já dado como emitido: a lista que veio com ele
  // já está filtrada, e avisar de novo no primeiro render dispararia uma
  // requisição repetida a cada visita à tela.
  const emitido = useRef(normalizar(defaultValue));

  useEffect(() => {
    const valor = normalizar(termoAdiado);
    if (valor === emitido.current) return;

    emitido.current = valor;
    buscaRef.current(valor, escopo);
    // Só a mudança do termo adiado dispara: escopo e limpeza avisam na hora,
    // pelos próprios handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAdiado]);

  // O foco vai para o campo assim que ele existe — no efeito, não logo após o
  // clique: no clique o campo ainda não foi montado. Quem clicou na lupa quer
  // digitar, e sem isto precisaria de um segundo clique.
  //
  // A primeira renderização fica de fora: com `defaultValue`, a barra já nasce
  // aberta, e roubar o foco para ela ao abrir a tela tiraria o usuário de onde
  // ele estava — ele não pediu para buscar, só voltou a uma lista já filtrada.
  const jaMontou = useRef(false);
  useEffect(() => {
    if (!jaMontou.current) {
      jaMontou.current = true;
      return;
    }
    if (aberto) campoRef.current?.focus();
  }, [aberto]);

  const abrir = () => setAberto(true);

  const limparEFechar = () => {
    setTermo("");
    if (alwaysOpen) campoRef.current?.focus();
    else setAberto(false);

    if (emitido.current !== "") {
      emitido.current = "";
      buscaRef.current("", escopo);
    }
  };

  const trocarEscopo = (novo: string) => {
    setEscopo(novo);
    // O `Select` devolve o foco ao próprio gatilho depois de escolher. Aqui o
    // destino natural é o campo: quem acabou de dizer "por CPF" quer digitar o
    // CPF. O `rAF` garante a ordem — sem ele, o foco do gatilho chegaria
    // depois e venceria.
    requestAnimationFrame(() => campoRef.current?.focus());

    // Trocar de escopo com texto digitado é uma pergunta nova — vale na hora.
    const proximo = scopes.find(item => item.value === novo);
    const valor = proximo?.parse ? proximo.parse(termo) : termo;
    emitido.current = valor;
    buscaRef.current(valor, novo);
  };

  const aoTeclar = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === "Escape") {
      evento.stopPropagation();
      limparEFechar();
    }
  };

  // Campo aberto e vazio que o usuário abandonou volta a ser um botão: um campo
  // de busca em branco no cabeçalho não diz nada e rouba a linha.
  //
  // Com a lista de escopo aberta, porém, o blur é de passagem: a opção clicada
  // não é focável, então o foco sai do campo no `mousedown` e só volta ao
  // seletor no clique. Fechar nesse intervalo tira a barra do usuário no meio
  // da escolha e custa dois cliques para reabrir. O `data-popover-aberto` do
  // gatilho do `Select` é o sinal de que a escolha ainda está em curso.
  const aoSairDaBusca = (evento: FocusEvent<HTMLDivElement>) => {
    if (alwaysOpen) return;
    if (evento.currentTarget.contains(evento.relatedTarget)) return;
    if (evento.currentTarget.querySelector("[data-popover-aberto]")) return;
    if (termo === "") setAberto(false);
  };

  const transicao = { duration: duracao(240), ease: EASE_OUT };

  // Sempre aberta e vazia, o botão vira só a lupa: não há o que limpar.
  const mostraLupa = alwaysOpen ? termo === "" : !aberto;

  return (
    // `h-11` fixo nos dois estados, e não `min-h`: a barra vive numa linha com
    // "Filtros" e "Gerar planilha", e um pixel de diferença na altura ao abrir
    // sacode a linha inteira. Com `border-box`, a borda que só existe aberta não
    // entra nessa conta.
    <motion.div
      layout
      transition={transicao}
      onBlur={aoSairDaBusca}
      className={cn(
        // Aberta, a barra é a MESMA moldura dos outros campos (`controlBase`):
        // mesma borda, mesmo raio, mesmo fundo. O foco vira `focus-within`
        // porque quem recebe o foco é o input lá dentro, não a barra.
        aberto && controlBase,
        "ease-ds-out flex h-11 items-center border transition-colors duration-150",
        aberto
          ? cn(
              "focus-within:border-ds-brand-500 focus-within:ring-ds-focus w-full focus-within:ring-2",
              !alwaysOpen && "sm:w-80"
            )
          : "rounded-ds-md w-11 border-transparent bg-transparent",
        "motion-reduce:transition-none",
        className
      )}
    >
      {/* O campo entra e sai sem `AnimatePresence`: a saída animada disputava a
          largura com o `layout` da barra, e era essa briga que fazia o ícone
          piscar no meio do caminho. Quem conta a história do fechamento é a
          largura encolhendo. */}
      {aberto ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: duracao(120) }}
          className="flex min-w-0 flex-1 items-center"
        >
          {scopes.length > 1 ? (
            <>
              <Select
                label={scopeLabel}
                hideLabel
                options={scopes}
                value={escopo}
                onValueChange={trocarEscopo}
                containerClassName="shrink-0"
                // Sem moldura própria: a moldura é a da barra inteira, senão
                // ficam duas bordas encostadas uma na outra.
                className="text-ds-small focus-visible:ring-ds-focus h-11 min-h-0 gap-1 rounded-none border-0 bg-transparent px-3 focus-visible:ring-2 focus-visible:ring-inset"
              />
              <span
                aria-hidden="true"
                className="bg-ds-border h-6 w-px shrink-0"
              />
            </>
          ) : null}

          <input
            ref={campoRef}
            id={`${idBase}-campo`}
            type="search"
            value={termo}
            inputMode={escopoAtual?.inputMode ?? "search"}
            aria-label={
              escopoAtual ? `${label} por ${escopoAtual.label}` : label
            }
            placeholder={escopoAtual?.placeholder ?? placeholder ?? "Buscar…"}
            autoComplete="off"
            onKeyDown={aoTeclar}
            onChange={evento => {
              const bruto = evento.target.value;
              setTermo(escopoAtual?.format ? escopoAtual.format(bruto) : bruto);
            }}
            className={cn(
              "font-ds-sans text-ds-body text-ds-ink-900 placeholder:text-ds-ink-600",
              "h-11 min-w-0 flex-1 bg-transparent px-3 outline-none",
              // O "x" nativo do type=search seria um segundo botão de limpar,
              // com aparência de cada navegador e sem fechar a barra.
              "[&::-webkit-search-cancel-button]:appearance-none"
            )}
          />
        </motion.div>
      ) : null}

      {/* Um botão só, nos dois estados: dois botões trocando de lugar é o que
          fazia a animação tropeçar. O que muda é a moldura e o ícone. */}
      <button
        type="button"
        onClick={
          alwaysOpen
            ? mostraLupa
              ? () => campoRef.current?.focus()
              : limparEFechar
            : aberto
              ? limparEFechar
              : abrir
        }
        aria-label={
          alwaysOpen
            ? mostraLupa
              ? label
              : "Limpar busca"
            : aberto
              ? "Limpar busca e fechar"
              : label
        }
        aria-expanded={alwaysOpen ? undefined : aberto}
        className={cn(
          "ease-ds-out focus-visible:ring-ds-focus inline-flex shrink-0 cursor-pointer items-center justify-center transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          "motion-reduce:transition-none",
          aberto
            ? "rounded-ds-md text-ds-ink-600 hover:text-ds-ink-900 focus-visible:ring-offset-ds-surface -mr-0.5 size-11 focus-visible:ring-offset-2"
            : "border-ds-border-strong bg-ds-surface text-ds-ink-700 hover:bg-ds-ink-50 hover:text-ds-ink-900 focus-visible:ring-offset-ds-paper size-11 rounded-full border focus-visible:ring-offset-2"
        )}
      >
        {/* Os dois ícones ficam empilhados e trocam por opacidade e giro. Montar
            e desmontar um de cada vez faria o ícone sumir por um quadro. */}
        <span className="relative grid size-4 place-items-center">
          <Search
            aria-hidden="true"
            className={cn(
              "ease-ds-out col-start-1 row-start-1 size-4 transition-all duration-150 motion-reduce:transition-none",
              mostraLupa
                ? "scale-100 rotate-0 opacity-100"
                : "scale-50 rotate-90 opacity-0"
            )}
          />
          <X
            aria-hidden="true"
            className={cn(
              "ease-ds-out col-start-1 row-start-1 size-4 transition-all duration-150 motion-reduce:transition-none",
              mostraLupa
                ? "scale-50 -rotate-90 opacity-0"
                : "scale-100 rotate-0 opacity-100"
            )}
          />
        </span>
      </button>
    </motion.div>
  );
};
